// Anonymous Buyer DNA session (localStorage) and the scoped claim marker (sessionStorage).
// Decision 2 of docs/plans/onboarding-one-path.md: a stale mid_anon_id alone is never claimed,
// because claiming copies the anon DNA over any account without an archetype. Only an unexpired
// marker, written by AccountCard right before register, password login or the Google redirect,
// drives the claim. Storage can throw (private mode, blocked site data): every access is guarded.
export const LS_ANON = 'mid_anon_id';
export const LS_SESSION = 'mid_anon_session';
export const CLAIM_KEY = 'mid_anon_claim';
const CLAIM_TTL_MS = 15 * 60 * 1000;

// store: 'localStorage' | 'sessionStorage'; reading window[store] itself throws when storage is blocked.
function readItem(store, key) {
  try { return window[store].getItem(key); } catch (_) { return null; }
}
function writeItem(store, key, value) {
  try { window[store].setItem(key, value); } catch (_) { /* storage blocked */ }
}
function removeItem(store, key) {
  try { window[store].removeItem(key); } catch (_) { /* storage blocked */ }
}

export const getAnon = () => ({ anonId: readItem('localStorage', LS_ANON), sessionId: readItem('localStorage', LS_SESSION) });

export function setAnon(anonId, sessionId) {
  writeItem('localStorage', LS_ANON, anonId);
  writeItem('localStorage', LS_SESSION, sessionId);
}

export function clearAnon() {
  removeItem('localStorage', LS_ANON);
  removeItem('localStorage', LS_SESSION);
}

/** method ('email' | 'google'): how the account was made, so the page can emit dna_account_done after a round trip. */
export function markClaim(anonId, method) {
  if (!anonId) return;
  writeItem('sessionStorage', CLAIM_KEY, JSON.stringify({
    anon_id: anonId, expires: Date.now() + CLAIM_TTL_MS, ...(method ? { method } : {}),
  }));
}

/** The marker {anon_id, expires, method?}, or null when missing, malformed or expired. */
export function peekClaim() {
  try {
    const marker = JSON.parse(readItem('sessionStorage', CLAIM_KEY));
    return marker?.anon_id && marker.expires > Date.now() ? marker : null;
  } catch (_) {
    return null;
  }
}

export const clearClaim = () => removeItem('sessionStorage', CLAIM_KEY);

// Terminal /dna/anon/claim rejections: 400 bad id, 403 session_claimed/buyer_only, 404 anon_profile_not_found.
const TERMINAL_CLAIM_STATUSES = [400, 403, 404];

/**
 * Claims the marked anon id for the signed-in account. `api` is the lib/api.js axios instance.
 * Success or terminal rejection clears the marker; a network error, 5xx or anything else keeps it
 * and resolves {claimed:false, retry:true}, so the next mount retries (decision 2d).
 */
export async function claimAnon(api) {
  const marker = peekClaim();
  if (!marker) return { claimed: false, retry: false };
  try {
    const { data } = await api.post('/dna/anon/claim', { anon_id: marker.anon_id });
    clearClaim();
    // Today a missing anon profile answers 200 {ok:false, reason:'anon_profile_not_found'}.
    return data?.ok === false ? { claimed: false, retry: false } : { claimed: true };
  } catch (error) {
    if (!TERMINAL_CLAIM_STATUSES.includes(error?.response?.status)) return { claimed: false, retry: true };
    clearClaim();
    return { claimed: false, retry: false };
  }
}
